// Mobile and Speed Optimization Audit Tool
type AuditValue = boolean | 'MANUAL_CHECK_REQUIRED' | { domContentLoaded: number | null; loadTime: number | null };

interface MobileSpeedAuditResult {
  mobile: Record<string, AuditValue>;
  speed: Record<string, AuditValue>;
  timestamp: string;
}

type PerformanceWithMemory = Performance & {
  memory?: {
    usedJSHeapSize: number;
  };
  timing?: {
    domContentLoadedEventEnd: number;
    loadEventEnd: number;
    navigationStart: number;
  };
};

export function runMobileSpeedAudit(): MobileSpeedAuditResult {
  const results: MobileSpeedAuditResult = {
    mobile: {},
    speed: {},
    timestamp: new Date().toISOString()
  };
  const perf = performance as PerformanceWithMemory;

  // 1. Responsive Layout (Mobile-First)
  const hasViewportMeta = document.querySelector('meta[name="viewport"]');
  const viewportContent = hasViewportMeta ? hasViewportMeta.getAttribute('content') : '';
  const isMobileFirst = !!viewportContent && viewportContent.includes('width=device-width') && viewportContent.includes('initial-scale=1');
  results.mobile.responsiveLayout = isMobileFirst;

  // 2. No Intrusive Interstitials
  const hasIntrusivePopups = document.querySelectorAll('[class*="popup"], [class*="modal"]').length > 0;
  results.mobile.noIntrusiveInterstitials = !hasIntrusivePopups;

  // 3. Light Page Weight
  const pageSize = perf.memory ? Math.round(perf.memory.usedJSHeapSize / 1024 / 1024) : 'Unknown';
  const isLightWeight = pageSize === 'Unknown' || pageSize < 50;
  results.mobile.lightWeight = isLightWeight;

  // 4. Responsive Images
  const images = document.querySelectorAll('img');
  const hasResponsiveImages = Array.from(images).some(img =>
    img.hasAttribute('srcset') || img.style.maxWidth === '100%'
  );
  results.mobile.responsiveImages = hasResponsiveImages;

  // 5. Optimized Image Formats
  const hasOptimizedImages = Array.from(images).some(img =>
    img.src.includes('.webp') || img.src.includes('.jpg')
  );
  results.mobile.optimizedImageFormats = hasOptimizedImages;

  // 6. Properly Sized Images
  const hasProperlySizedImages = Array.from(images).every(img => {
    const naturalWidth = img.naturalWidth;
    const displayWidth = img.offsetWidth;
    return naturalWidth <= displayWidth * 2;
  });
  results.mobile.properlySizedImages = hasProperlySizedImages;

  // 7. Google Search Console - Manual check
  results.mobile.googleSearchConsole = 'MANUAL_CHECK_REQUIRED';

  // 8. Mobile UX Issues Fixed
  const hasMobileNavigation = document.querySelector('.hamburger_btn');
  const hasTouchFriendlyElements = document.querySelectorAll('button, a').length > 0;
  results.mobile.mobileUX = hasMobileNavigation && hasTouchFriendlyElements;

  // 9. Thumb-Friendly Navigation
  const navElements = document.querySelectorAll('.hamburger_btn, .sidenav .btn');
  const isThumbFriendly = Array.from(navElements).every(el => {
    const rect = el.getBoundingClientRect();
    return rect.width >= 44 && rect.height >= 44;
  });
  results.mobile.thumbFriendlyNavigation = isThumbFriendly;

  // 10. Mobile Optimized Videos
  const videos = document.querySelectorAll('video');
  const hasMobileOptimizedVideos = Array.from(videos).every(video =>
    video.hasAttribute('preload') && video.getAttribute('preload') === 'none'
  );
  results.mobile.mobileOptimizedVideos = hasMobileOptimizedVideos;

  // 11. Easy Tap Targets
  const clickableElements = document.querySelectorAll('button, a, input');
  const hasEasyTapTargets = Array.from(clickableElements).every(el => {
    const rect = el.getBoundingClientRect();
    return rect.width >= 44 && rect.height >= 44;
  });
  results.mobile.easyTapTargets = hasEasyTapTargets;

  // 12. Favicon in Mobile SERPs
  const hasFavicon = document.querySelector('link[rel="icon"]');
  results.mobile.faviconInMobileSERPs = !!hasFavicon;

  // 13. Content Parity
  const hasMetaDescription = document.querySelector('meta[name="description"]');
  const hasTitle = document.title && document.title !== 'Loading...';
  results.mobile.contentParity = hasMetaDescription && hasTitle;

  // 14. Real Device Testing - Manual check
  results.mobile.realDeviceTesting = 'MANUAL_CHECK_REQUIRED';

  // 1. Above-the-Fold Optimization
  const aboveFoldElements = document.querySelectorAll('header, .header, .welcome-message');
  const hasAboveFoldOptimization = aboveFoldElements.length > 0;
  results.speed.aboveFoldOptimization = hasAboveFoldOptimization;

  // 2. Core Web Vitals
  const hasPerformanceAPI = 'performance' in window;
  results.speed.coreWebVitals = hasPerformanceAPI;

  // 3. Speed Traps Fixed
  const hasAsyncScripts = Array.from(document.querySelectorAll('script')).every(script =>
    script.hasAttribute('async') || script.hasAttribute('defer') || script.src === ''
  );
  results.speed.speedTrapsFixed = hasAsyncScripts;

  // 4. Comprehensive Speed Audit
  const domContentLoaded = perf.timing ?
    perf.timing.domContentLoadedEventEnd - perf.timing.navigationStart : null;
  const loadTime = perf.timing ?
    perf.timing.loadEventEnd - perf.timing.navigationStart : null;

  results.speed.comprehensiveAudit = { domContentLoaded, loadTime };

  // 5-6. Search Console Checks - Manual
  results.speed.desktopCoreWebVitals = 'MANUAL_CHECK_REQUIRED';
  results.speed.mobileCoreWebVitals = 'MANUAL_CHECK_REQUIRED';

  // 7. Meet Core Web Vitals Thresholds
  const meetsThresholds = domContentLoaded < 2000 && loadTime < 3000;
  results.speed.meetsThresholds = meetsThresholds;

  return results as MobileSpeedAuditResult;
}

// Auto-run audit when page loads
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    setTimeout(() => {
      runMobileSpeedAudit();
    }, 1000);
  });
}

